import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Scanner,
  prepareZXingModule,
  type IDetectedBarcode,
  type IScannerError,
  type IScannerHandle,
} from '@yudiel/react-qr-scanner'
import type { BarcodeFormat } from 'barcode-detector/ponyfill'
import { BarcodeDetector } from 'barcode-detector/ponyfill'
import { normalizeBarcode } from '@/lib/barcode'

const SCAN_FORMATS: BarcodeFormat[] = [
  'qr_code',
  'data_matrix',
  'code_128',
  'code_39',
  'ean_13',
  'ean_8',
  'upc_a',
  'upc_e',
]

interface LiveBarcodeProps {
  onDetect: (code: string) => void
  onError?: (message: string) => void
  deviceId?: string
  paused?: boolean
  className?: string
}

export function scannerErrorMessage(error: IScannerError) {
  switch (error.kind) {
    case 'permission-denied':
      return 'Camera permission was blocked. Allow camera access, or type the code.'
    case 'no-camera':
      return 'No camera was found. Type the code or upload a photo.'
    case 'in-use':
      return 'The camera is already in use by another app.'
    case 'insecure-context':
      return 'The camera needs a secure page (https or localhost).'
    case 'overconstrained':
      return 'This camera could not start. Try the other camera.'
    default:
      return error.message || 'Could not read the QR code.'
  }
}

function isScannerControl(target: EventTarget | null) {
  return target instanceof Element && Boolean(target.closest('button, input, a, label, [role="slider"]'))
}

function mapCoverPoint(video: HTMLVideoElement, rect: DOMRect, localX: number, localY: number) {
  if (!video.videoWidth || !video.videoHeight || !rect.width || !rect.height) {
    return {
      x: Math.min(1, Math.max(0, localX / (rect.width || 1))),
      y: Math.min(1, Math.max(0, localY / (rect.height || 1))),
    }
  }

  const videoRatio = video.videoWidth / video.videoHeight
  const boxRatio = rect.width / rect.height
  let x = localX / rect.width
  let y = localY / rect.height

  if (videoRatio > boxRatio) {
    const displayedWidth = rect.height * videoRatio
    const offset = (displayedWidth - rect.width) / 2
    x = (localX + offset) / displayedWidth
  } else {
    const displayedHeight = rect.width / videoRatio
    const offset = (displayedHeight - rect.height) / 2
    y = (localY + offset) / displayedHeight
  }

  return {
    x: Math.min(1, Math.max(0, x)),
    y: Math.min(1, Math.max(0, y)),
  }
}

async function applyAdvanced(track: MediaStreamTrack, advanced: Record<string, unknown>) {
  await track.applyConstraints({ advanced: [advanced] } as MediaTrackConstraints)
}

async function enableContinuousFocus(track: MediaStreamTrack) {
  try {
    track.contentHint = 'detail'
  } catch {
    // Older browsers omit contentHint.
  }

  const attempts: Record<string, unknown>[] = [
    { focusMode: 'continuous', exposureMode: 'continuous', whiteBalanceMode: 'continuous' },
    { focusMode: 'continuous', exposureMode: 'continuous' },
    { focusMode: 'continuous' },
  ]

  for (const advanced of attempts) {
    try {
      await applyAdvanced(track, advanced)
      return true
    } catch {
      // Try the next, smaller set. Some cameras reject unknown keys.
    }
  }
  return false
}

async function focusAt(track: MediaStreamTrack, x: number, y: number) {
  const point = { x, y }
  const attempts: Record<string, unknown>[] = [
    { focusMode: 'single-shot', pointsOfInterest: [point] },
    { focusMode: 'continuous', pointsOfInterest: [point] },
    { pointsOfInterest: [point] },
    { focusMode: 'single-shot' },
    { focusMode: 'continuous' },
  ]

  for (const advanced of attempts) {
    try {
      await applyAdvanced(track, advanced)
      return true
    } catch {
      // Keep trying until one constraint set is accepted.
    }
  }
  return false
}

async function readFrame(video: HTMLVideoElement) {
  if (!video.videoWidth || !video.videoHeight) return ''
  const canvas = document.createElement('canvas')
  canvas.width = video.videoWidth
  canvas.height = video.videoHeight
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return ''
  context.drawImage(video, 0, 0, canvas.width, canvas.height)

  try {
    let codes = await new BarcodeDetector({ formats: ['qr_code', 'data_matrix'] }).detect(canvas)
    if (!codes[0]?.rawValue) {
      codes = await new BarcodeDetector({ formats: ['any'] }).detect(canvas)
    }
    return normalizeBarcode(codes[0]?.rawValue || '')
  } catch {
    return ''
  }
}

export function LiveBarcode({ onDetect, onError, deviceId, paused, className }: LiveBarcodeProps) {
  const scannerRef = useRef<IScannerHandle>(null)
  const onDetectRef = useRef(onDetect)
  const lastRef = useRef('')
  const tapSeq = useRef(0)
  const [ring, setRing] = useState<{ x: number; y: number } | null>(null)
  const [hint, setHint] = useState('Tap the code to focus')
  onDetectRef.current = onDetect

  useEffect(() => {
    void prepareZXingModule({ fireImmediately: true })
  }, [])

  useEffect(() => {
    let stopped = false
    let tries = 0
    let timer = 0
    const arm = () => {
      if (stopped) return
      const track = scannerRef.current?.getStream()?.getVideoTracks()[0]
      if (track && track.readyState === 'live') {
        void enableContinuousFocus(track)
        return
      }
      tries += 1
      if (tries < 30) timer = window.setTimeout(arm, 200)
    }
    timer = window.setTimeout(arm, 250)
    return () => {
      stopped = true
      window.clearTimeout(timer)
    }
  }, [deviceId, paused])

  const constraints = useMemo<MediaTrackConstraints>(() => (
    deviceId
      ? { deviceId: { exact: deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } }
      : { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }
  ), [deviceId])

  const deliver = (raw: string) => {
    const code = normalizeBarcode(raw)
    if (!code || code === lastRef.current) return
    lastRef.current = code
    onDetectRef.current(code)
  }

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isScannerControl(event.target)) return
    const rect = event.currentTarget.getBoundingClientRect()
    const localX = event.clientX - rect.left
    const localY = event.clientY - rect.top
    setRing({ x: localX, y: localY })
    setHint('Focusing…')

    const video = scannerRef.current?.getVideoElement() || null
    const track = scannerRef.current?.getStream()?.getVideoTracks()[0]
    const point = video
      ? mapCoverPoint(video, rect, localX, localY)
      : { x: localX / rect.width, y: localY / rect.height }
    const seq = tapSeq.current + 1
    tapSeq.current = seq

    void (async () => {
      const focused = track ? await focusAt(track, point.x, point.y) : false
      await new Promise((resolve) => window.setTimeout(resolve, 650))
      if (tapSeq.current !== seq) return
      const code = video ? await readFrame(video) : ''
      if (tapSeq.current !== seq) return
      if (code) {
        setHint('Code read')
        deliver(code)
        return
      }
      if (track && focused) {
        void enableContinuousFocus(track)
      }
      setHint(focused
        ? 'Focused. Hold the code steady.'
        : 'This camera cannot refocus. Move the code a little farther away.')
    })()
  }

  return (
    <div className={`relative ${className || ''}`} onPointerDown={onPointerDown}>
      <Scanner
        ref={scannerRef}
        paused={paused}
        sound={false}
        allowMultiple={false}
        formats={SCAN_FORMATS}
        retryDelay={40}
        settleDelayMs={150}
        startTimeoutMs={8000}
        constraints={constraints}
        components={{ finder: true, torch: true, zoom: true, onOff: false }}
        styles={{
          container: { width: '100%', height: '100%', aspectRatio: 'auto' },
          video: { width: '100%', height: '100%', objectFit: 'cover' },
        }}
        onScan={(codes: IDetectedBarcode[]) => deliver(codes[0]?.rawValue || '')}
        onError={(error) => onError?.(scannerErrorMessage(error))}
      />
      {ring && (
        <span
          className="pointer-events-none absolute z-20 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-md border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.45)]"
          style={{ left: ring.x, top: ring.y }}
        />
      )}
      <p className="pointer-events-none absolute inset-x-0 top-2 z-20 px-3 text-center text-[11px] font-medium text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]">
        {hint}
      </p>
    </div>
  )
}
